import React, { memo, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { SelectedTrackType, Video } from 'react-native-video';

import { useNavigation } from '@react-navigation/native';

import { InnerBorder } from '@/components/InnerBorder';
import { Text } from '@/components/TextComponent';
import { Box, Image } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { ArcadeLobbyTabNavigationProp } from '@/navigation/arcade/types';
import { common, designSystem, withOpacity } from '@/styles/styles';

import { GameWithMetadata } from '../hooks/use-arcade-games';
import { CardBlur } from './CardBlur';

type ArcadeGameCardProps = {
    game: GameWithMetadata;
    shouldPlayVideo: boolean;
};

export const ArcadeGameCard = memo(({ game, shouldPlayVideo }: ArcadeGameCardProps) => {
    const navigation = useNavigation<ArcadeLobbyTabNavigationProp>();
    const backgroundColor = game.metadata.card_bg_color;
    const [videoFailed, setVideoFailed] = useState(false);
    return (
        <TouchableOpacity onPress={() => navigation.navigate('ArcadeGameDetailsScreen', { game })}>
            <Box
                height={280}
                p="s16"
                mb="s16"
                style={[{ backgroundColor }, styles.container]}
                borderRadius="r20"
                overflow="hidden"
            >
                <InnerBorder
                    borderRadius="r20"
                    borderColor={ds.colorValue.border.subtle}
                    boxProps={{ style: common.zIndex1 }}
                />
                <CardBlur />
                <Box style={[styles.shadow, { backgroundColor: backgroundColor ?? designSystem.colors.neutral800 }]}>
                    <Box style={styles.videoContainer}>
                        <InnerBorder
                            borderRadius="r16"
                            borderColor={ds.colorValue.border.strong}
                            boxProps={{ style: common.zIndex1 }}
                        />
                        {shouldPlayVideo && game.metadata.video && !videoFailed ? (
                            <Video
                                source={{ uri: game.metadata.video }}
                                renderLoader={() => (
                                    <Image source={{ uri: game.metadata.lobby_image }} style={styles.video} />
                                )}
                                style={styles.video}
                                repeat={true}
                                disableFocus={true}
                                volume={0}
                                controls={false}
                                ignoreSilentSwitch="obey"
                                selectedAudioTrack={{
                                    type: SelectedTrackType.DISABLED,
                                }}
                                onError={() => setVideoFailed(true)}
                            />
                        ) : game.metadata.lobby_image ? (
                            <Image source={{ uri: game.metadata.lobby_image }} style={styles.video} />
                        ) : null}
                    </Box>
                </Box>
                <Box position="absolute" bottom={16} left={16} style={common.zIndex1}>
                    <Text
                        color={ds.colorToken.text.primary}
                        variant="headlineSmall"
                        letterSpacing={0}
                        fontWeight="bold"
                    >
                        {game.name}
                    </Text>
                </Box>
                {backgroundColor ? (
                    <LinearGradient
                        colors={[backgroundColor, backgroundColor, withOpacity(backgroundColor, 0)]}
                        style={styles.gradient}
                        angle={180}
                        positions={[0, 0.3, 1]}
                    />
                ) : null}
            </Box>
        </TouchableOpacity>
    );
});

const styles = StyleSheet.create({
    container: {
        borderCurve: 'continuous',
    },
    video: {
        width: '100%',
        height: '100%',
    },
    shadow: {
        shadowColor: designSystem.colors.neutralBlack,
        shadowOpacity: 0.4,
        shadowRadius: 24,
        elevation: 1,
        shadowOffset: { width: 0, height: 0 },
        borderRadius: 16,
        borderCurve: 'continuous',
    },
    videoContainer: {
        overflow: 'hidden',
        borderRadius: 16,
        borderCurve: 'continuous',
    },
    gradient: {
        position: 'absolute',
        bottom: -1,
        left: -1,
        right: -1,
        height: 140,
    },
});
